import { isNMIHostedEnabled } from './nmiGateway';

export const toTrimmedString = (value) => String(value || '').trim();

function getFields(screen) {
    if (!screen) {
        return [];
    }

    if (Array.isArray(screen.fields)) {
        return screen.fields;
    }

    if (screen.field) {
        return [screen.field];
    }

    return [];
}

export function getOrderSummaryValidationErrors(value) {
    const checkout = value || {};
    const errors = {};
    const usesNMIHosted = checkout.useNMIHosted ?? isNMIHostedEnabled();
    const cardNumberDigits = String(checkout.cardNumber || '').replace(/\D/g, '');
    const cvvDigits = String(checkout.cvv || '').replace(/\D/g, '');
    const isExpiryValid = /^(0[1-9]|1[0-2])\/\d{2}$/.test(String(checkout.expiry || ''));

    if (!String(checkout.cardholderName || '').trim()) {
        errors.cardholderName = 'Cardholder Name is required';
    }
    if (!usesNMIHosted) {
        if (cardNumberDigits.length < 15) {
            errors.cardNumber = 'Please enter a valid card number';
        }
        if (!isExpiryValid) {
            errors.expiry = 'Enter MM/YY';
        }
        if (cvvDigits.length < 3) {
            errors.cvv = 'Enter CVV';
        }
    } else if (!String(checkout.paymentToken || '').trim()) {
        errors.paymentToken = 'Please complete card payment details to continue';
    }

    if (!checkout.billingSameAsMailing) {
        if (!String(checkout.billingAddress1 || '').trim()) {
            errors.billingAddress1 = 'Street address is required';
        }
        if (!String(checkout.billingCity || '').trim()) {
            errors.billingCity = 'City is required';
        }
        if (!String(checkout.billingState || '').trim()) {
            errors.billingState = 'State is required';
        }
        if (String(checkout.billingZip || '').replace(/\D/g, '').length < 5) {
            errors.billingZip = 'Enter a valid ZIP';
        }
    }

    return errors;
}

export const DOB_ERRORS_CONSTANTS = {
    dob: 'Please complete date of birth to continue',
    month: 'Please enter birth month to continue',
    day: 'Please enter birth day to continue',
    year: 'Please enter birth year to continue',
    invalidMonth: 'Please enter a valid birth month to continue',
    invalidDay: 'Please enter a valid birth day to continue',
    invalidYear: 'Please enter a valid birth year to continue',
    invalidDate: 'Please enter a valid date of birth',
};

export function isCompleteDob(dob) {
    return Boolean(dob?.month && dob?.day && dob?.year);
}

export function getDobError(dob) {
    const month = String(dob?.month || '').trim();
    const day = String(dob?.day || '').trim();
    const year = String(dob?.year || '').trim();
    const nowYear = new Date().getFullYear();

    if (!month && !day && !year) {
        return DOB_ERRORS_CONSTANTS.dob;
    }
    if (!month) {
        return DOB_ERRORS_CONSTANTS.month;
    }
    if (!day) {
        return DOB_ERRORS_CONSTANTS.day;
    }
    if (!year) {
        return DOB_ERRORS_CONSTANTS.year;
    }

    if (!/^\d+$/.test(month)) {
        return DOB_ERRORS_CONSTANTS.invalidMonth;
    }
    if (!/^\d+$/.test(day)) {
        return DOB_ERRORS_CONSTANTS.invalidDay;
    }
    if (!/^\d{4}$/.test(year)) {
        return DOB_ERRORS_CONSTANTS.invalidYear;
    }

    const monthNumber = Number(month);
    const dayNumber = Number(day);
    const yearNumber = Number(year);

    if (!Number.isInteger(monthNumber) || monthNumber < 1 || monthNumber > 12) {
        return DOB_ERRORS_CONSTANTS.invalidMonth;
    }
    if (!Number.isInteger(dayNumber) || dayNumber < 1 || dayNumber > 31) {
        return DOB_ERRORS_CONSTANTS.invalidDay;
    }
    if (!Number.isInteger(yearNumber) || yearNumber < 1900 || yearNumber > nowYear) {
        return DOB_ERRORS_CONSTANTS.invalidYear;
    }

    const date = new Date(yearNumber, monthNumber - 1, dayNumber);
    if (date.getFullYear() !== yearNumber) {
        return DOB_ERRORS_CONSTANTS.invalidYear;
    }
    if (date.getMonth() !== monthNumber - 1 || date.getDate() !== dayNumber) {
        return DOB_ERRORS_CONSTANTS.invalidDay;
    }

    return '';
}

export function isValidDob(dob) {
    return !getDobError(dob);
}

export function validateField(field, answers) {
    const value = answers?.[field.id];

    if (field.type === 'orderSummary') {
        const orderSummaryErrors = getOrderSummaryValidationErrors(value);
        return Object.values(orderSummaryErrors)[0] || '';
    }

    if (field.type === 'termsAcceptance') {
        if (field.required && value !== true) {
            return field.requiredMessage || 'Please accept the terms to continue';
        }
        return '';
    }

    if (field.type === 'choiceCards') {
        const selectedValue = value && typeof value === 'object' ? value.selected : value;

        if (field.required && !selectedValue) {
            return field.requiredMessage || 'Please select an option';
        }

        if (field.optInDetails && selectedValue === field.optInDetails.showForValue) {
            const details = value?.details || {};
            const dob = details.dateOfBirth || answers?.relayBankDateOfBirth || {};
            const dobError = getDobError(dob);
            if (dobError) {
                return dobError;
            }

            const employeeCount = field.id === 'relayBank' ? answers?.relayEmployeeCount : details.employeeCount;
            if (!employeeCount) {
                return 'Please select employee count to continue';
            }
        }

        return '';
    }

    if (field.type === 'dateOfBirth') {
        const selectedStateCode = answers?.stateOfFormation?.stateCode || '';
        const requiredStateCodes = Array.isArray(field.requiredWhenStateCodes) ? field.requiredWhenStateCodes : [];
        const isRequiredForState = requiredStateCodes.includes(selectedStateCode);

        if (!isRequiredForState) {
            return '';
        }

        const dobError = getDobError(value);
        if (dobError) {
            return dobError;
        }

        return '';
    }

    if (field.type === 'checkbox') {
        if (field.required && value !== true) {
            return field.requiredMessage || 'This checkbox is required';
        }
        return '';
    }

    const trimmedValue = String(value || '').trim();
    const normalizedLabel = field.label?.split('(')[0]?.trim() || 'This field';
    const requiredLabel = normalizedLabel.charAt(0).toUpperCase() + normalizedLabel.slice(1).toLowerCase();

    if (field.required && !trimmedValue) {
        return `${requiredLabel} is required`;
    }

    if (field.minLength && trimmedValue.length < field.minLength) {
        return `${field.label} must be at least ${field.minLength} characters`;
    }

    if ((field.type === 'email' || field.validation === 'email') && trimmedValue) {
        const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue);
        if (!validEmail) {
            return field.validationMessage || 'Please enter a valid email address';
        }
    }

    if ((field.type === 'phone' || field.validation === 'usPhone') && trimmedValue) {
        const digitCount = trimmedValue.replace(/\D/g, '').length;
        if (digitCount < 10) {
            return field.validationMessage || 'Please enter a valid 10-digit phone number';
        }
    }

    if (field.validation === 'zip5' && trimmedValue) {
        const digitCount = trimmedValue.replace(/\D/g, '').length;
        if (digitCount < 5) {
            return field.validationMessage || 'Enter a valid 5-digit ZIP';
        }
    }

    return '';
}

export function validateScreen(screen, answers) {
    const errors = {};
    const fields = getFields(screen);

    fields.forEach((field) => {
        const message = validateField(field, answers);
        if (message) {
            errors[field.id] = message;
        }
    });

    return errors;
}

export function scrollToFieldAndFocus(fieldId, options = {}) {
    if (!fieldId || typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    const {
        selectorTemplate = '[data-online-cart-field="{fieldId}"]',
        fallbackToElementId = false,
        behavior = 'smooth',
        block = 'center',
        inline = 'nearest',
        focusTargetSelector = 'input, textarea, button, [role="combobox"], [tabindex]:not([tabindex="-1"]):not([data-content="true"])',
        focusAnchorIfFocusable = false,
        deferredScroll = false
    } = options;

    const selector = String(selectorTemplate || '').replace('{fieldId}', String(fieldId));
    const fieldAnchor = (selector ? document.querySelector(selector) : null) || (fallbackToElementId ? document.getElementById(String(fieldId)) : null);

    // debugger
    if (!fieldAnchor) {
        return;
    }

    if (deferredScroll) {
        setTimeout(() => {
            fieldAnchor.scrollIntoView({
                behavior,
                block,
                inline,
            });
        }, 100);
    } else {
        fieldAnchor.scrollIntoView({
            behavior,
            block,
            inline,
        });
    }


    window.requestAnimationFrame(() => {
        const dobErrorTarget = ['dateOfBirth', 'relayBank'].includes(fieldId) ? fieldAnchor.querySelector?.('[data-online-cart-dob-part="error"]') : null;
        const focusTarget =
            dobErrorTarget || fieldAnchor.querySelector?.(focusTargetSelector) || (focusAnchorIfFocusable && typeof fieldAnchor.focus === 'function' ? fieldAnchor : null);
        if (focusTarget && typeof focusTarget.focus === 'function') {
            focusTarget.focus({ preventScroll: true });
        }
    });
}

const normalizeStreetForCompare = (value) =>
    toTrimmedString(value)
        .toLowerCase()
        .replace(/[.,]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

// Smarty returns the unit inside delivery_line_1 (e.g. "500 N Lake Shore Dr Apt 2201") while the
// form keeps street and unit in separate fields. Merge both lines on each side before comparing so
// the same address split differently is not counted as a change.
const mergeStreetLines = (line1, line2) => normalizeStreetForCompare(`${toTrimmedString(line1)} ${toTrimmedString(line2)}`);

export const areAddressesDifferent = (inputAddress = {}, suggestionAddress = {}) => {
    const inputStreet = mergeStreetLines(inputAddress.addressLine1, inputAddress.addressLine2);
    const suggestedStreet = mergeStreetLines(suggestionAddress.addressLine1, suggestionAddress.addressLine2);

    const inputCity = normalizeStreetForCompare(inputAddress.city);
    const suggestedCity = normalizeStreetForCompare(suggestionAddress.city);

    const inputZip = toTrimmedString(inputAddress.zipCode).replace(/\D/g, '').slice(0, 5);
    const suggestedZip = toTrimmedString(suggestionAddress.zipCode).replace(/\D/g, '').slice(0, 5);

    const inputStateCode = toTrimmedString(inputAddress.stateCode).toLowerCase();
    const suggestedStateCode = toTrimmedString(suggestionAddress.stateCode).toLowerCase();

    return (
        inputStreet !== suggestedStreet ||
        (inputZip && suggestedZip && inputZip !== suggestedZip) ||
        (inputCity && suggestedCity && inputCity !== suggestedCity) ||
        (inputStateCode && suggestedStateCode && inputStateCode !== suggestedStateCode)
    );
};

const normalizeAddressInput = (payload = {}) => {
    return {
        addressLine1: toTrimmedString(payload.addressLine1 || ''),
        addressLine2: toTrimmedString(payload.addressLine2 || ''),
        city: toTrimmedString(payload.city || ''),
        // state: toTrimmedString(payload.state || ''),
        stateCode: toTrimmedString(payload.stateCode || ''),
        zipCode: toTrimmedString(payload.zipCode || ''),
    };
};

export { getFields };
